import { useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";

import { appConfig } from "@/config";
import { useLanguage } from "@/contexts/language";
import { isNative } from "@/lib/capacitor";
import { hapticSelection } from "@/lib/haptics";

// "Go back" gestures for the app shell:
//
//   - an edge swipe, tracked in JS, for iOS (WKWebView ships with its own
//     back-forward gesture disabled) and for browsers, and
//   - the Android system back gesture/button, which Capacitor delivers as an
//     App plugin `backButton` event rather than as touches — so the JS gesture
//     never sees it and this hook handles both.
//
// The page follows the finger and snaps back when the swipe is too short, the
// way every native stack navigator behaves. Anything the driver would lose by
// navigating away (a dialog, a drawer) closes first instead of the route
// changing underneath it.

/** How close to the leading edge a touch must start, in px. */
const EDGE_ZONE = 28;
/** Movement before the gesture commits to horizontal (or gives up to a scroll). */
const LOCK_SLOP = 12;
/** Fraction of the screen that counts as "far enough" on release. */
const COMMIT_RATIO = 0.32;
/** …or this many px thrown fast enough (px per ms). */
const FLICK_DISTANCE = 60;
const FLICK_VELOCITY = 0.4;
const SETTLE_MS = 220;

/**
 * An open dialog/drawer owns the gesture: back should dismiss it, not navigate.
 * Both Radix and vaul close on Escape, which also runs their exit animation.
 */
function dismissOverlay(): boolean {
  const overlay = document.querySelector(
    '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]',
  );
  if (!overlay) return false;
  document.dispatchEvent(
    new KeyboardEvent("keydown", { key: "Escape", bubbles: true }),
  );
  return true;
}

/**
 * Where "back" goes with no history to pop — a thread opened straight from a
 * push notification is a whole stack on its own. `/chats/abc` → `/chats`;
 * top-level tabs return null, so back does nothing there (and Android
 * minimizes instead).
 */
function parentPath(pathname: string): string | null {
  const base = appConfig.routerBasePath === "/" ? "" : appConfig.routerBasePath;
  const path =
    base && pathname.startsWith(base) ? pathname.slice(base.length) : pathname;
  const segments = path.split("/").filter(Boolean);
  if (segments.length < 2) return null;
  return `/${segments.slice(0, -1).join("/")}`;
}

export function useSwipeBack() {
  const router = useRouter();
  const { dir } = useLanguage();
  const rtl = dir === "rtl";
  const contentRef = useRef<HTMLDivElement | null>(null);

  const canGoBack = useCallback(
    () =>
      router.history.canGoBack() ||
      parentPath(router.state.location.pathname) !== null,
    [router],
  );

  const goBack = useCallback(() => {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    const parent = parentPath(router.state.location.pathname);
    if (parent) void router.navigate({ to: parent, replace: true });
  }, [router]);

  // --- Android system back -------------------------------------------------
  useEffect(() => {
    if (!isNative()) return;
    let remove: (() => void) | null = null;
    let disposed = false;

    void import("@capacitor/app")
      .then(async ({ App }) => {
        const handle = await App.addListener("backButton", () => {
          if (dismissOverlay()) return;
          if (canGoBack()) {
            goBack();
            return;
          }
          // At the root of the stack Android expects the app to go to the
          // background, not to close.
          void App.minimizeApp().catch(() => undefined);
        });
        if (disposed) void handle.remove();
        else remove = () => void handle.remove();
      })
      .catch(() => undefined);

    return () => {
      disposed = true;
      remove?.();
    };
  }, [canGoBack, goBack]);

  // --- Edge swipe ----------------------------------------------------------
  useEffect(() => {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let startX = 0;
    let startY = 0;
    let startedAt = 0;
    let distance = 0;
    let tracking = false;
    let locked = false;

    const paint = (offset: number) => {
      const node = contentRef.current;
      if (!node) return;
      node.style.transition = "";
      node.style.transform = offset
        ? `translate3d(${rtl ? -offset : offset}px, 0, 0)`
        : "";
      // A shadow along the leading edge sells the page as a sheet lifting off.
      node.style.boxShadow = offset ? "0 0 24px rgba(0, 0, 0, 0.28)" : "";
    };

    /** Clears every inline style so `position: fixed` children behave again. */
    const clear = () => {
      const node = contentRef.current;
      if (!node) return;
      node.style.transition = "";
      node.style.transform = "";
      node.style.boxShadow = "";
      node.style.willChange = "";
    };

    const settle = (to: number, then?: () => void) => {
      const node = contentRef.current;
      if (!node || reduceMotion) {
        clear();
        then?.();
        return;
      }
      node.style.transition = `transform ${SETTLE_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`;
      node.style.transform = to ? `translate3d(${rtl ? -to : to}px, 0, 0)` : "";
      window.setTimeout(() => {
        clear();
        then?.();
      }, SETTLE_MS);
    };

    const onTouchStart = (event: TouchEvent) => {
      tracking = false;
      locked = false;
      distance = 0;
      if (event.touches.length !== 1) return;
      if (!canGoBack()) return;

      const touch = event.touches[0]!;
      const fromEdge = rtl
        ? touch.clientX >= window.innerWidth - EDGE_ZONE
        : touch.clientX <= EDGE_ZONE;
      if (!fromEdge) return;

      // Opt-out for anything that owns horizontal drags of its own.
      const target = event.target as Element | null;
      if (target?.closest?.("[data-no-swipe-back]")) return;
      if (
        document.querySelector(
          '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"]',
        )
      ) {
        return;
      }

      startX = touch.clientX;
      startY = touch.clientY;
      startedAt = event.timeStamp;
      tracking = true;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!tracking) return;
      const touch = event.touches[0];
      if (!touch) return;

      // Inward from the leading edge is positive in both directions.
      const dx = rtl ? startX - touch.clientX : touch.clientX - startX;
      const dy = Math.abs(touch.clientY - startY);

      if (!locked) {
        if (dy > LOCK_SLOP && dy > dx) {
          // The driver is scrolling the list, not leaving the page.
          tracking = false;
          return;
        }
        if (dx < LOCK_SLOP) return;
        locked = true;
        const node = contentRef.current;
        if (node) node.style.willChange = "transform";
      }

      distance = Math.max(0, dx);
      // Held: stop the browser from also scrolling or over-scrolling.
      event.preventDefault();
      paint(distance);
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (!tracking || !locked) {
        tracking = false;
        return;
      }
      tracking = false;
      locked = false;

      const width = window.innerWidth;
      const elapsed = Math.max(1, event.timeStamp - startedAt);
      const committed =
        distance > width * COMMIT_RATIO ||
        (distance > FLICK_DISTANCE && distance / elapsed > FLICK_VELOCITY);

      if (!committed) {
        settle(0);
        return;
      }
      hapticSelection();
      // Finish the slide, then navigate: the incoming page mounts at rest.
      settle(width, goBack);
    };

    const onTouchCancel = () => {
      if (!tracking) return;
      tracking = false;
      locked = false;
      settle(0);
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchCancel, { passive: true });

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchCancel);
      clear();
    };
  }, [canGoBack, goBack, rtl]);

  return { contentRef };
}
